import { useEffect, useState } from 'react';
import { Copy, Gift, Loader2, MessageCircle, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api.get('/referrals/me').then((res) => { if (live) setData(res.data); })
      .catch((e) => { if (live) setError(e.response?.data?.detail || 'Could not load your referral link.'); });
    return () => { live = false; };
  }, []);

  if (error) return null;
  if (!data) return <div className="py-5" data-testid="referral-card-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const link = `${window.location.origin}/signup/pharmacy?ref=${data.referral_code}`;
  const waText = encodeURIComponent(`Hi! I use DutiLyne to run my pharmacy — it's great for stock, sales and patient care. Sign up with my link for extra free trial days: ${link}`);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success('Referral link copied'); }
    catch { toast.error('Could not copy. Select and copy the link shown here.'); }
  };

  return (
    <section className="space-y-4 py-5 border-t min-w-0" data-testid="referral-card">
      <div>
        <h2 className="text-lg font-semibold flex gap-2 items-center"><Gift className="w-5 h-5" />Refer another pharmacy</h2>
        <p className="text-sm font-medium text-emerald-700" data-testid="referral-card-explainer">
          Share your link with other pharmacy owners. New pharmacies who sign up with it get
          <b> {data.signup_bonus_days} extra trial days</b> right away, and once that pharmacy
          becomes a paying customer, <b>you</b> get <b>{data.referrer_bonus_days} extra free days</b> —
          no cash, just more time on DutiLyne.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
        <span className="text-sm break-all flex-1 min-w-0 font-mono" data-testid="referral-link-value">{link}</span>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="icon" title="Copy referral link" onClick={copyLink} data-testid="referral-link-copy"><Copy className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" asChild title="Share on WhatsApp" className="text-emerald-600 border-emerald-600/40 hover:bg-emerald-600/10">
            <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" data-testid="referral-whatsapp-share"><MessageCircle className="w-4 h-4" /></a>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <Badge variant="secondary" className="gap-1.5" data-testid="referral-stat-referred"><Users className="w-3.5 h-3.5" /> {data.referred_count} referred</Badge>
        <Badge variant="secondary" className="gap-1.5" data-testid="referral-stat-converted">{data.converted_count} converted</Badge>
        <Badge variant="secondary" className="gap-1.5" data-testid="referral-stat-bonus-days">{data.bonus_days_earned} bonus days earned</Badge>
      </div>

      {data.pharmacies.length > 0 && (
        <div className="space-y-1 pr-2 sm:pr-0" data-testid="referral-pharmacy-list">
          {data.pharmacies.map((p) => (
            <div key={p.tenant_id} className="flex items-center justify-between gap-3 border-t py-2 text-sm" data-testid={`referral-pharmacy-row-${p.tenant_id}`}>
              <span className="truncate">{p.name}</span>
              <Badge variant={p.converted ? 'default' : 'outline'} className="shrink-0" data-testid={`referral-pharmacy-status-${p.tenant_id}`}>{p.converted ? 'Paying customer' : p.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
